import React, { useEffect, useMemo, useState } from 'react';

import { TryOnButton } from '../../try-on/TryOnButton';
import { TryOnModal } from '../../try-on/TryOnModal';
import type { TryOnProduct } from '../../../types/tryOn';
import { SkeletonProductCard } from './SkeletonProductCard';
import { localizeProductUrl } from '../../../utils/market-product-url';
import { t } from '../../../utils/ui-strings';
import {
  COLLAPSED_SIZE_COUNT,
  type ProductSize,
  parseSizeOptions,
  splitSizeList,
} from './size-utils';

interface Product {
  id?: string;
  variantId?: string;
  selectionKey?: string;
  image?: string;
  brand?: string;
  name: string;
  size?: string;
  sizes?: ProductSize[] | string;
  tagLabel?: string;
  reason?: string;
  price?: string | number;
  currency?: string;
  regularPrice?: number;
  discountPercent?: number;
  url?: string;
  color?: string;
  material?: string;
  /** Kategori-slot från backend (kavaj/skjorta/byxa/skor/accessoar…). */
  slot?: string;
  /** Try-on-behörighet, avgjord i backend. Klienten bedömer aldrig själv. */
  tryOn?: boolean;
  /** Bara för skor; saknas = helkropp. */
  tryOnFraming?: 'feet';
  actionButtons?: Array<{ label: string; value: string; requiresSize?: boolean }>;
}

/**
 * Behörigheten avgörs i backend (try-on-eligibility.ts) på samma
 * kategoriupplösning som hjärnan använder, och följer med som `tryOn` på
 * kortet. Här fanns tidigare en handskriven svensk namn-regex plus en
 * slot-lista — den kunde aldrig hålla jämna steg med katalogen och avvek
 * dessutom från backends egen grind.
 */
const supportsTryOn = (product: Product): boolean =>
  Boolean(product.image && (product.id || product.url) && product.tryOn);

const toTryOnProduct = (product: Product): TryOnProduct => ({
  id: product.id ?? product.url ?? product.name,
  variantId: product.variantId ?? product.id ?? product.url ?? product.name,
  name: product.name,
  imageUrl: product.image ?? '',
  brand: product.brand,
  price: product.price,
  currency: product.currency,
  url: product.url,
  color: product.color,
  material: product.material,
  size: product.size,
  sizes: product.sizes,
  framing: product.tryOnFraming ?? 'full',
});

interface ProductCardProps {
  product: Product;
  identityKey: string;
  onActionClick?: (value: string) => void;
  onTryOn?: (product: Product) => void;
  lang?: string;
}

const isCartAction = (value: string) =>
  value.startsWith('cart.add:') || value.startsWith('outfit.cart.add:');

const appendSizeToActionValue = (value: string, size: string) => {
  if (!isCartAction(value)) return value;

  const separator = value.includes('?') ? '&' : '?';
  return `${value}${separator}size=${encodeURIComponent(size)}`;
};

const buildProductIdentityKey = (product: Product, index: number) =>
  String(product.selectionKey ?? product.id ?? product.url ?? `${product.name}-${index}`);

export function ProductCard({ product, identityKey, onActionClick, onTryOn, lang }: ProductCardProps) {
  const sizeOptions = useMemo(() => parseSizeOptions(product), [product]);
  const [showAllSizes, setShowAllSizes] = useState(false);
  const [selectedSize, setSelectedSize] = useState('');
  const hasOverflowSizes = sizeOptions.length > COLLAPSED_SIZE_COUNT;
  const selectedSizeIndex = sizeOptions.findIndex((size) => size.rawValue === selectedSize);

  useEffect(() => {
    setSelectedSize('');
    setShowAllSizes(false);
  }, [identityKey]);

  useEffect(() => {
    if (!selectedSize) return;
    if (sizeOptions.some((size) => size.rawValue === selectedSize)) return;
    setSelectedSize('');
  }, [selectedSize, sizeOptions]);

  const visibleSizeOptions = useMemo(() => {
    if (!hasOverflowSizes || showAllSizes) return sizeOptions;

    const collapsed = sizeOptions.slice(0, COLLAPSED_SIZE_COUNT);
    if (selectedSizeIndex < 0 || selectedSizeIndex < COLLAPSED_SIZE_COUNT) return collapsed;

    return [...sizeOptions.slice(0, COLLAPSED_SIZE_COUNT - 1), sizeOptions[selectedSizeIndex]];
  }, [hasOverflowSizes, selectedSizeIndex, showAllSizes, sizeOptions]);

  return (
    <article className="h-full overflow-hidden rounded-lg border border-black/10 bg-white">
      {product.image && (
        <img
          src={product.image}
          alt={product.name}
          className="block aspect-[4/5] w-full shrink-0 bg-white object-cover object-top"
        />
      )}

      <div className="flex grow flex-col whitespace-normal p-3">
        {product.brand && (
          <p className="mb-1 text-sm font-medium uppercase tracking-[0.05em] text-brand-blue">
            {product.brand}
          </p>
        )}

        <p className="mb-2 text-sm font-medium leading-[1.35] text-brand-blue">{product.name}</p>

        {product.reason && (
          <p className="mb-2 text-xs italic leading-[1.4] text-text-grey">{product.reason}</p>
        )}

        {product.tagLabel && (
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.05em] text-text-grey">
            {product.tagLabel}
          </p>
        )}

        {sizeOptions.length > 0 ? (
          <div className="mb-3">
            <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.05em] text-text-grey">
              {t('select_size', lang)}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {visibleSizeOptions.map((size) => {
                const active = selectedSize === size.rawValue;
                return (
                  <button
                    key={size.rawValue}
                    type="button"
                    disabled={!size.buyable}
                    onClick={() => setSelectedSize(size.rawValue)}
                    title={size.rawValue}
                    aria-label={size.rawValue}
                    className={[
                      'min-w-9 rounded-sm border px-2 py-1 text-xs transition-colors',
                      active
                        ? 'border-brand-blue bg-brand-blue text-white'
                        : 'border-black/15 bg-white text-brand-blue hover:border-brand-blue',
                      !size.buyable ? 'cursor-not-allowed opacity-40 line-through' : '',
                    ].join(' ')}
                  >
                    {size.displayLabel}
                  </button>
                );
              })}
            </div>
            {hasOverflowSizes ? (
              <button
                type="button"
                onClick={() => setShowAllSizes((current) => !current)}
                className="mt-2 text-xs font-medium text-brand-blue underline underline-offset-2"
              >
                {showAllSizes
                  ? t('show_less', lang)
                  : `${t('show_more', lang)} (${sizeOptions.length - COLLAPSED_SIZE_COUNT})`}
              </button>
            ) : null}
          </div>
        ) : product.size ? (
          <p className="mb-2 text-xs text-text-grey">
            {t('size_prefix', lang)}:{' '}
            <span className="font-medium text-brand-blue">
              {(() => {
                const sizes = splitSizeList(product.size!);
                if (sizes.length <= 3) return product.size;
                return `${sizes.slice(0, 3).join(', ')} + ${sizes.length - 3}`;
              })()}
            </span>
          </p>
        ) : null}

        {product.price &&
          (product.regularPrice && product.discountPercent ? (
            <p className="mb-3 flex flex-wrap items-baseline gap-2 text-base">
              <span className="font-semibold text-red-700">
                {product.price} {product.currency || ''}
              </span>
              <span className="text-sm text-text-grey line-through">
                {product.regularPrice} {product.currency || ''}
              </span>
              <span className="rounded bg-red-700 px-1.5 py-0.5 text-xs font-semibold text-white">
                −{product.discountPercent}%
              </span>
            </p>
          ) : (
            <p className="mb-3 text-base font-medium text-brand-blue">
              {product.price} {product.currency || ''}
            </p>
          ))}

        {product.url ||
        (product.actionButtons && product.actionButtons.length > 0 && onActionClick) ||
        (supportsTryOn(product) && onTryOn) ? (
          <div className="mt-auto flex flex-col gap-2">
            {product.actionButtons && product.actionButtons.length > 0 && onActionClick
              ? product.actionButtons.map((actionButton, index) => {
                  const needsSize = actionButton.requiresSize || isCartAction(actionButton.value);
                  const disabled = needsSize && sizeOptions.length > 0 && !selectedSize;
                  return (
                    <button
                      key={`${actionButton.value}-${index}`}
                      type="button"
                      disabled={disabled}
                      onClick={() => {
                        const nextValue = selectedSize
                          ? appendSizeToActionValue(actionButton.value, selectedSize)
                          : actionButton.value;
                        onActionClick(nextValue);
                      }}
                      className="inline-block w-full rounded border border-brand-blue px-3 py-2 text-center text-[11px] uppercase tracking-[0.05em] text-brand-blue transition-colors duration-200 hover:bg-brand-blue hover:text-white disabled:cursor-not-allowed disabled:border-black/15 disabled:text-text-grey disabled:hover:bg-white"
                    >
                      {disabled ? t('select_size', lang) : actionButton.label}
                    </button>
                  );
                })
              : null}

            {supportsTryOn(product) && onTryOn ? (
              <TryOnButton onClick={() => onTryOn(product)} lang={lang} />
            ) : null}

            {product.url ? (
              <a
                href={localizeProductUrl(product.url, lang)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block rounded border border-brand-blue bg-brand-blue px-3 py-2 text-center text-[11px] uppercase tracking-[0.05em] text-white no-underline transition-colors duration-200 hover:bg-white hover:text-brand-blue"
              >
                {t('view_product', lang)}
              </a>
            ) : null}
          </div>
        ) : null}
      </div>
    </article>
  );
}

interface ProductSliderProps {
  products: Product[];
  onActionClick?: (value: string) => void;
  lang?: string;
  expectedCount?: number;
}

export function ProductSlider({
  products,
  onActionClick,
  expectedCount,
  lang,
}: ProductSliderProps) {
  // Slidern äger EN modal för hela renderingen, så att kunden kan byta plagg i
  // resultatvyn med samma uppladdade bild — en modal per kort låste flödet.
  const [tryOnProduct, setTryOnProduct] = useState<Product | null>(null);

  const skeletonCount =
    typeof expectedCount === 'number' && expectedCount > products.length
      ? expectedCount - products.length
      : 0;

  if (!products.length && skeletonCount <= 0) return null;

  const tryOnCandidates = products.filter(supportsTryOn).map(toTryOnProduct);

  return (
    <div className="my-2 w-full whitespace-normal">
      <div className="flex snap-x snap-mandatory gap-3 overflow-x-auto pb-2">
        {products.map((product, idx) => {
          const identityKey = buildProductIdentityKey(product, idx);
          return (
            <div
              key={identityKey}
              className="w-[78%] min-w-[220px] max-w-[300px] shrink-0 snap-start"
            >
              <ProductCard
                product={product}
                identityKey={identityKey}
                onActionClick={onActionClick}
                onTryOn={setTryOnProduct}
                lang={lang}
              />
            </div>
          );
        })}
        {Array.from({ length: skeletonCount }).map((_, idx) => (
          <div
            key={`skeleton-${idx}`}
            className="w-[78%] min-w-[220px] max-w-[300px] shrink-0 snap-start"
          >
            <SkeletonProductCard />
          </div>
        ))}
      </div>

      {tryOnProduct ? (
        <TryOnModal
          product={toTryOnProduct(tryOnProduct)}
          products={tryOnCandidates}
          isOpen
          lang={lang}
          onActionClick={onActionClick}
          onClose={() => setTryOnProduct(null)}
        />
      ) : null}
    </div>
  );
}
